export interface EventoKitPeca {
  readonly defId: string;
  readonly qtd: number;
  readonly equipado: boolean;
}

export interface EventoAtual {
  readonly estado: number;
  readonly inscrito: boolean;
  readonly moeda: 'silver' | 'gold' | string;
  readonly taxa: number;
  readonly bolo: number;
  readonly inscritos: number;
  readonly fechaEmMs: number;
  readonly fimEmMs: number;
  readonly vivos: number;
  readonly portaAberta: boolean;
  readonly modo: string;
  readonly kit: readonly EventoKitPeca[];
  readonly trava: string | null;
  readonly inscricaoSegundos: number;
  readonly partidaSegundos: number;
  readonly daemonSegundos: number;
  readonly pares: number;
  /** techspec-eventos: o agendado tem sala de até 6 e pódio. */
  readonly agendado?: boolean;
  readonly porSala?: number;
  readonly minimo?: number;
  readonly podio?: readonly number[];
  readonly queimaPct?: number;
}

/** Mais › Eventos (techspec-eventos §02.2). */
export interface EventoProximo {
  readonly id: string;
  readonly hora: string;
  readonly emMs: number;
  /** O tipo do evento (`ultimate-battle`); sem ele, é o Ultimate Battle. */
  readonly tipo?: string;
}

export interface EventoDropItem {
  readonly def: string;
  readonly qtdMin: number;
  readonly qtdMax: number;
  /** Chance final em %, já com o peso. */
  readonly chance: number;
}

export interface EventoRaridade {
  readonly id: string;
  readonly chance: number;
  readonly itens: readonly EventoDropItem[];
}

export interface EventoTabela {
  readonly id: string;
  readonly nome: string;
  readonly raridades: readonly EventoRaridade[];
  readonly garantiaEventos: number;
  readonly garantiaRaridade: string | null;
}

export interface EventoPremio {
  readonly id: number;
  readonly def: string;
  readonly qtd: number;
  readonly raridade: string;
  readonly garantia: boolean;
  readonly emMs: number;
}

export interface EventoAgenda {
  readonly proximos: readonly EventoProximo[];
  readonly inscricao: number;
  readonly moeda: string;
  readonly porSala: number;
  readonly minimo: number;
  readonly podio: readonly number[];
  readonly queimaPct: number;
  readonly sorteiosPorDia: number;
  readonly sorteiosHoje: number;
  readonly garantiaContador: number;
  readonly tabelas: readonly EventoTabela[];
  readonly pendentes: readonly EventoPremio[];
  readonly versao: number;
  /** techspec-eventos §08: os horários de hoje (passados também) e a janela do dia de Brasília, para a faixa do quadro. */
  readonly hoje?: readonly EventoProximo[];
  readonly diaInicioMs?: number;
  readonly diaFimMs?: number;
  readonly inscricaoSegundos?: number;
  readonly partidaSegundos?: number;
  readonly daemonSegundos?: number;
  readonly avisoSegundos?: number;
  readonly kit?: readonly EventoKitPeca[];
}

export interface EventoEstado {
  readonly estado: number;
  readonly inscrito: boolean;
  readonly moeda: string;
  readonly taxa: number;
  readonly bolo: number;
  readonly inscritos: number;
  readonly fechaEmMs: number;
  readonly fimEmMs: number;
  readonly vivos: number;
  readonly portaAberta: boolean;
  readonly modo: string;
}

export interface EventoAbrirBody {
  readonly taxa?: number;
  readonly inscricaoMin?: number;
  readonly partidaMin?: number;
  readonly daemonMin?: number;
  readonly modo?: string;
}

// ---------------------------------------------------------------- Chefe do Mundo (techspec-chefe-mundo §07)

/** A barra no topo: vem pelo hub (`ChefeEstado`) a cada 2 s enquanto ele vive. `id` vazio: não há chefe. */
export interface ChefeEstado {
  readonly id: string;
  /** 0 vivo, 1 morto, 2 fugiu. */
  readonly estado: number;
  readonly variante: string;
  readonly nome: string;
  readonly matiz: number;
  readonly mapa: string;
  /** Vida em milésimos, 0..1000. */
  readonly permil: number;
  readonly fimEmMs: number;
  readonly lutadores: number;
}

export interface ChefeRank {
  readonly lugar: number;
  readonly nome: string;
  readonly dano: number;
  readonly pct: number;
}

export interface ChefeAoVivo extends ChefeEstado {
  readonly ranking: readonly ChefeRank[];
  readonly meuLugar: number;
  readonly meuDano: number;
  readonly danoMinimo: number;
  readonly vidaMax: number;
}

export interface ChefeAnuncio {
  readonly horarioId: string;
  readonly emMs: number;
  readonly variante: string;
  readonly nome: string;
  readonly matiz: number;
  readonly mapa: string;
}

export interface ChefeRegras {
  readonly duracaoSegundos: number;
  readonly avisoSegundos: number;
  readonly vidaBase: number;
  readonly vidaPorJogador: number;
  readonly ajusteSegundos: number;
  readonly chuvaSegundos: number;
  readonly chuvaAlcance: number;
  readonly chuvaPct: number;
  readonly invocaFases: readonly number[];
  readonly invocaQtd: number;
  readonly furiaPct: number;
  readonly furiaGolpePct: number;
  readonly danoMinimoPct: number;
  readonly prataMin: number;
  readonly prataMax: number;
  readonly fragmentos: number;
  readonly fragmentosGolpeFinal: number;
  readonly fragmentosTop3: number;
  readonly capaBonus: number;
  readonly capaRepetidaFragmentos: number;
}

export interface ChefeVariante {
  readonly id: string;
  readonly nome: string;
  readonly matiz: number;
  readonly mapa: string;
  readonly ativa: boolean;
  /** Chance dentro do mapa, em %. */
  readonly chancePct: number;
  /** Ids das montarias da cor (`hiryu-brasa`). */
  readonly montarias: readonly string[];
}

export interface ChefeMapa {
  readonly mapa: string;
  readonly ativo: boolean;
  readonly tabela: EventoTabela | null;
}

export interface AltarOferta {
  readonly id: string;
  readonly item: string;
  readonly nome: string;
  readonly qtd: number;
  readonly preco: number;
  /** A faixa da cor; vazio = Geral. */
  readonly variante: string;
  readonly destaque: boolean;
  readonly limitePeriodo: string;
  readonly limiteQtd: number;
  readonly usadas: number;
  readonly estoquePeriodo: string;
  readonly estoqueQtd: number;
  /** -1 = sem estoque. */
  readonly restam: number;
  readonly deMs: number | null;
  readonly ateMs: number | null;
  /** limite · esgotado · jaTem · fora · null */
  readonly bloqueio: string | null;
}

export interface ChefeAltar {
  readonly moeda: string;
  readonly saldo: number;
  readonly ofertas: readonly AltarOferta[];
  /** O Baú do Balron: a chance de cada raridade de tinta (comum = básicas, raro/épico/lendário = prime). */
  readonly bau?: readonly { readonly raridade: string; readonly chance: number; readonly tintas: readonly string[] }[];
}

export interface Chefe {
  readonly aoVivo: ChefeAoVivo | null;
  readonly proximos: readonly EventoProximo[];
  readonly anunciado: ChefeAnuncio | null;
  readonly regras: ChefeRegras;
  readonly variantes: readonly ChefeVariante[];
  readonly mapas: readonly ChefeMapa[];
  readonly altar: ChefeAltar;
  /** As capas exclusivas que o personagem já tem (`capa-balron-rubro`). */
  readonly capas: readonly string[];
  readonly garantiaContador: number;
  readonly versao: number;
}

export interface AltarTroca {
  readonly oferta: string;
  readonly entregue: string;
  readonly qtd: number;
  readonly saldo: number;
  /** A capa foi direto para o guarda-roupa (não passa por Prêmios). */
  readonly guardaRoupa: boolean;
  /** A raridade do que saiu do Baú (comum · raro · epico · lendario), ou `altar`. */
  readonly raridade?: string;
}

/** O prefixo da capa exclusiva e o item "na cor" das tabelas do chefe (o mesmo do `ChefeArte`). */
export const PREFIXO_CAPA = 'capa-balron-';
export const TOKEN_CAPA = '{capa}';
export const FRAGMENTO = 'fragmento-alma';
export const BAU = 'bau-balron';

// ------------------------------------------------------------------ o Pentagrama das Cinco Orbes (techspec-pentagrama)

export const TIPO_PENTAGRAMA = 'pentagrama';
export const PREFIXO_FRAGMENTO_PENTA = 'pent-fragmento-';
export const PREFIXO_ORBE_PENTA = 'pent-orbe-';
export const ROBE_PENTA = 'shroud';

/** A edição ao vivo (evento `PentaEstado` do hub). `cor` −1: nenhuma ponta desperta. */
export interface PentaEstado {
  readonly id: string;
  readonly cor: number;
  /** 0 ondas, 1 Guardião, 2 encerrada. */
  readonly fase: number;
  readonly guardiaoEmMs: number;
  readonly fimEmMs: number;
  readonly guardiaoMorto: boolean;
  readonly lutadores: number;
}

export interface PentaAltarLinha { readonly nome: string; readonly mascara: number; readonly ritual: boolean }

/** O canal parado da forja (1) ou do ritual (2): `ateMs` é quando termina; null = não há. */
export interface PentaCanal { readonly tipo: 1 | 2; readonly cor: number; readonly ms: number; readonly ateMs: number }

export interface PentaRegras {
  readonly ativo: boolean;
  readonly duracaoSegundos: number;
  readonly guardiaoAosSegundos: number;
  readonly avisoSegundos: number;
  readonly danoMinimo: number;
  readonly fragmentosParticipar: number;
  readonly fragmentosGuardiao: number;
  readonly fragmentosTop3: number;
  readonly fragmentosPorOrbe: number;
  readonly prataPorOrbe: number;
  readonly prataRitual: number;
  readonly forjaSegundos: number;
  readonly ritualSegundos: number;
  readonly robeBonus: number;
  readonly ordem: readonly string[];
}

export interface PentaProximo { readonly id: string; readonly emMs: number; readonly hora: string; readonly cor: string }

export interface PentaAoVivo {
  readonly id: string;
  readonly cor: string;
  readonly fase: number;
  readonly guardiaoEmMs: number;
  readonly fimEmMs: number;
  readonly guardiaoMorto: boolean;
  readonly lutadores: number;
  readonly meuDano: number;
  readonly danoMinimo: number;
}

export interface Penta {
  readonly regras: PentaRegras;
  readonly proximos: readonly PentaProximo[];
  readonly aoVivo: PentaAoVivo | null;
  readonly temRobe: boolean;
  readonly mural: readonly { readonly nome: string; readonly emMs: number }[];
}
